import axios, { AxiosInstance } from 'axios';
import authorizeService from '../../../Identity/Utilities/AuthorizeService';

/**
 * Opaque Kendo Spreadsheet workbook payload. We don't introspect it — we just persist
 * whatever the Spreadsheet's `saveJSON()` returns and feed it back via `fromJSON()`.
 */
export type WorkbookJson = Record<string, unknown>;

/** Lock metadata returned alongside every GET / save response. */
export interface WorkbookLock {
  userId: string;
  userName: string;
  /** Unix-epoch default ("1970-01-01T00:00:00Z") when unlocked. */
  lockedAt: string;
}

/**
 * Wrapped workbook response shape from the BFF. Returned by GET and the PUT save.
 * The `data` field holds the decompressed spreadsheet JSON (what we feed into Spreadsheet.fromJSON).
 */
export interface SiteWorkbookReadDto {
  id: string;
  name: string;
  uncompressedSize: number;
  createdBy: string;
  createdByName: string;
  created: string;
  updated: string;
  lock: WorkbookLock;
  data: WorkbookJson;
}

class SiteWorkbookApiService {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL,
    });
  }

  private async getAuthorizedHeaders(includeJsonContentType = false) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
    };
    if (includeJsonContentType) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  async getSiteWorkbook(): Promise<SiteWorkbookReadDto> {
    const response = await this.axiosInstance.get<SiteWorkbookReadDto>(
      'web/v1/workbooks',
      { headers: await this.getAuthorizedHeaders() },
    );
    return response.data;
  }

  /**
   * Persist the workbook. Backend verifies lock ownership and releases the lock on success.
   * Returns 423 Locked if the caller doesn't currently hold the lock.
   *
   * Body is `{ Name, Data }` — `Name` is required (a 400 "Name is required" comes back without it).
   */
  async saveSiteWorkbook(data: WorkbookJson, name: string): Promise<SiteWorkbookReadDto> {
    const response = await this.axiosInstance.put<SiteWorkbookReadDto>(
      'web/v1/workbooks',
      { Name: name, Data: data },
      { headers: await this.getAuthorizedHeaders(true) },
    );
    return response.data;
  }

  /** Acquire the lock (Edit click). 409 Conflict if someone else already holds it. */
  async acquireLock(): Promise<SiteWorkbookReadDto> {
    const response = await this.axiosInstance.post<SiteWorkbookReadDto>(
      'web/v1/workbooks/lock',
      {},
      { headers: await this.getAuthorizedHeaders(true) },
    );
    return response.data;
  }

  /** Release the lock without saving (Cancel click). 403 if the caller doesn't own the lock. */
  async releaseLock(): Promise<SiteWorkbookReadDto> {
    const response = await this.axiosInstance.delete<SiteWorkbookReadDto>(
      'web/v1/workbooks/lock',
      { headers: await this.getAuthorizedHeaders() },
    );
    return response.data;
  }

  /** Force-release the lock — restricted server-side to Manager / SuperAdmin / AccountAdmin. */
  async forceReleaseLock(): Promise<SiteWorkbookReadDto> {
    const response = await this.axiosInstance.delete<SiteWorkbookReadDto>(
      'web/v1/workbooks/lock/force',
      { headers: await this.getAuthorizedHeaders() },
    );
    return response.data;
  }
}

const siteWorkbookApiService = new SiteWorkbookApiService();
export default siteWorkbookApiService;
